import React, { useCallback, useEffect, useState } from 'react';
import { Button, Text } from '@rsos/base-components';
import { useFlag } from '@rsos/flags';
import AudioDebugPanel from './AudioDebugPanel';
import { StyledDebugMenu } from './DebugMenu.styles';
import { FlagDebugPanel } from './FlagDebugPanel';
import { LocationDebugPanel } from './LocationDebugPanel';

interface DebugMenuProps {
  onError?: (error: string) => void;
}

interface DebugButton {
  id: string;
  label: string;
  component: React.ComponentType<{ onClose: () => void }>;
}

const DEBUG_MENU_HOT_KEY = 'ALT+SHIFT+D';

const DebugMenu: React.FC<DebugMenuProps> = ({ onError }) => {
  const isDebugPanelEnabled = useFlag('debug-panel');
  const [isDebugPanelVisible, setIsDebugPanelVisible] = useState(false);
  const [activePanel, setActivePanel] = useState<string | null>(null);

  const toggleVisible = useCallback(
    (e: KeyboardEvent) => {
      // NOTE: if you update the key update DEBUG_MENU_HOT_KEY
      if (e.altKey && e.shiftKey && e.code === 'KeyD') {
        setIsDebugPanelVisible(v => !v);
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [setIsDebugPanelVisible],
  );

  useEffect(() => {
    if (isDebugPanelEnabled) {
      window.addEventListener('keydown', toggleVisible);
    }
    return () => {
      window.removeEventListener('keydown', toggleVisible);
    };
  }, [toggleVisible, isDebugPanelEnabled]);

  if (!isDebugPanelEnabled || !isDebugPanelVisible) {
    return null;
  }

  const panels: DebugButton[] = [
    {
      id: 'audio-debug',
      label: 'Realtime Audio',
      component: (props: { onClose: () => void }) => (
        <AudioDebugPanel onError={onError} {...props} />
      ),
    },
    {
      id: 'location-debug',
      label: 'Plot Location',
      component: (props: { onClose: () => void }) => (
        <LocationDebugPanel {...props} />
      ),
    },
    {
      id: 'flag-debug',
      label: 'Feature Flags',
      component: (props: { onClose: () => void }) => (
        <FlagDebugPanel {...props} />
      ),
    },
  ];

  return (
    <StyledDebugMenu>
      <Text size="small" color="primaryText">
        DEBUG MENU
        <br />({DEBUG_MENU_HOT_KEY} to hide)
      </Text>
      {panels.map(panel => (
        <div key={panel.id}>
          <Button
            onClick={() => setActivePanel(panel.id)}
            btnType="tertiary"
            noMargin
          >
            {panel.label}
          </Button>
          {panel.id === activePanel && (
            <panel.component onClose={() => setActivePanel(null)} />
          )}
        </div>
      ))}
    </StyledDebugMenu>
  );
};

export default DebugMenu;
